import dayjs from 'dayjs';

// Preset definitions with IDs for time range picker
export const TIME_RANGE_PRESETS = [
  { id: 'last-10m', label: 'Letzte 10 Minuten', start: 'now-10m', end: 'now', maxDays: 0.01 },
  { id: 'last-30m', label: 'Letzte 30 Minuten', start: 'now-30m', end: 'now', maxDays: 0.02 },
  { id: 'last-1h', label: 'Letzte 1 Stunde', start: 'now-1h', end: 'now', maxDays: 0.04 },
  { id: 'last-2h', label: 'Letzte 2 Stunden', start: 'now-2h', end: 'now', maxDays: 0.08 },
  { id: 'last-6h', label: 'Letzte 6 Stunden', start: 'now-6h', end: 'now', maxDays: 0.25 },
  { id: 'last-12h', label: 'Letzte 12 Stunden', start: 'now-12h', end: 'now', maxDays: 0.5 },
  { id: 'last-24h', label: 'Letzte 24 Stunden', start: 'now-24h', end: 'now', maxDays: 1 },
  { id: 'last-2d', label: 'Letzte 2 Tage', start: 'now-2d', end: 'now', maxDays: 2 },
  { id: 'last-7d', label: 'Letzte 7 Tage', start: 'now-7d', end: 'now', maxDays: 7 },
  { id: 'last-30d', label: 'Letzte 30 Tage', start: 'now-30d', end: 'now', maxDays: 30 },
  { id: 'last-90d', label: 'Letzte 90 Tage', start: 'now-90d', end: 'now', maxDays: 90 },
];

/**
 * Parse time expression like "now-1h", "now-30m", "2024-01-01T10:00"
 * @param {string} expr - Time expression to parse
 * @returns {dayjs.Dayjs|null} Parsed dayjs object or null if invalid
 */
export const parseTimeExpression = (expr) => {
  if (!expr) return null;

  try {
    // Handle "now" expressions
    if (expr === 'now') {
      return dayjs();
    }

    if (expr.startsWith('now-')) {
      const timeStr = expr.substring(4); // Remove "now-"
      const now = dayjs();

      // Parse different units: 1h, 30m, 2d, etc.
      const match = timeStr.match(/^(\d+)([mhd])$/);
      if (match) {
        const [, amount, unit] = match;
        const unitMap = { m: 'minute', h: 'hour', d: 'day' };
        return now.subtract(parseInt(amount), unitMap[unit]);
      }

      // Parse complex expressions like "6h30m"
      const complexMatch = timeStr.match(/^(?:(\d+)d)?(?:(\d+)h)?(?:(\d+)m)?$/);
      if (complexMatch) {
        const [, days, hours, minutes] = complexMatch;
        let result = now;
        if (days) result = result.subtract(parseInt(days), 'day');
        if (hours) result = result.subtract(parseInt(hours), 'hour');
        if (minutes) result = result.subtract(parseInt(minutes), 'minute');
        return result;
      }
    }

    // Handle absolute timestamps
    const parsed = dayjs(expr);
    if (parsed.isValid()) {
      return parsed;
    }

    return null;
  } catch (e) {
    return null;
  }
};

/**
 * Calculate absolute timestamps for a preset ID
 * @param {string} presetId - ID of the preset (e.g., 'last-7d')
 * @returns {{from_time: string, to_time: string}|null} Calculated timestamps or null if invalid
 */
export const calculatePresetRange = (presetId) => {
  const preset = TIME_RANGE_PRESETS.find(p => p.id === presetId);
  if (!preset) return null;

  const startParsed = parseTimeExpression(preset.start);
  const endParsed = parseTimeExpression(preset.end);

  if (!startParsed || !endParsed) return null;

  return {
    from_time: startParsed.format('YYYY-MM-DD HH:mm:ss'),
    to_time: endParsed.format('YYYY-MM-DD HH:mm:ss')
  };
};
